import React, { useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { NRSLogo, NRSWordmark } from "../components/NRSLogo";
import { useT, LangToggle } from "../lib/i18n";
import { getPostAuthTarget } from "../lib/postAuthNavigation";

export default function Login() {
  const { user, login } = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const signedInPath = (account) => getPostAuthTarget(account, location.state?.from);
  if (user) return <Navigate to={signedInPath(user)} replace />;

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    setErr("");
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) nav(signedInPath(r.user), { replace: true });
    else setErr(r.error);
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="auth-side hidden lg:flex flex-col justify-between p-12 text-white">
        <div className="relative z-10 flex items-center gap-3">
          <NRSLogo size={36} mono />
          <div className="flex flex-col leading-tight">
            <NRSWordmark className="text-sm" />
            <span className="max-w-64 text-[9px] text-slate-400">{t("header.tagline")}</span>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="text-5xl font-extrabold tracking-tight leading-[1.05]">{t("login.hero.title")}</h1>
          <p className="text-slate-300 mt-6 max-w-md leading-relaxed">{t("login.hero.sub")}</p>
          <div className="mt-12 grid grid-cols-3 gap-6 text-xs font-mono text-slate-300 border-t border-[#1E2C42] pt-6">
            <div><div className="text-white text-2xl font-extrabold tracking-tight">22</div>{t("login.stat.rules")}</div>
            <div><div className="text-white text-2xl font-extrabold tracking-tight">17</div>{t("login.stat.hazards")}</div>
            <div><div className="text-white text-2xl font-extrabold tracking-tight">∞</div>{t("login.stat.audit")}</div>
          </div>
        </div>
        <div className="relative z-10 text-[10px] text-slate-500 font-mono tracking-wider">{t("login.footer_badge")}</div>
      </div>
      <div className="flex flex-col p-8 bg-[#0E1626]">
        <div className="flex justify-end">
          <LangToggle />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
            <div className="lg:hidden flex items-center gap-2 mb-2">
              <NRSLogo size={28} />
              <div className="flex flex-col leading-tight">
                <NRSWordmark />
                <span className="text-[9px] text-[var(--text-2)]">{t("header.tagline")}</span>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-extrabold tracking-tight">{t("login.title")}</h2>
              <p className="text-sm text-slate-400 mt-1">{t("login.sub")}</p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold tracking-wide uppercase text-slate-400">{t("login.email")}</label>
                <input data-testid="login-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full border border-[#1E2C42] px-3 py-2 rounded-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 focus:outline-none transition-colors" />
              </div>
              <div>
                <label className="text-xs font-semibold tracking-wide uppercase text-slate-400">{t("login.password")}</label>
                <input data-testid="login-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 w-full border border-[#1E2C42] px-3 py-2 rounded-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 focus:outline-none transition-colors" />
                <div className="mt-2 text-right">
                  <Link to="/forgot-password" className="text-xs font-semibold text-cyan-400 hover:underline" data-testid="login-forgot-password">
                    {t("auth.forgot.link")}
                  </Link>
                </div>
              </div>
            </div>
            {err && <div data-testid="login-error" className="text-sm text-red-400 border border-red-500/30 bg-red-950/40 px-3 py-2">{err}</div>}
            <button data-testid="login-submit" disabled={loading} className="btn-primary w-full py-2.5">
              {loading ? t("login.submitting") : t("login.submit")}
            </button>
            <div className="text-sm text-slate-400">{t("login.no_account")} <Link className="text-cyan-400 hover:underline font-semibold" to="/register" state={location.state} data-testid="login-to-register">{t("login.register")}</Link></div>
          </form>
        </div>
      </div>
    </div>
  );
}
